/**
 * AppointmentsTable Shared Types
 *
 * Shared types and utilities for displaying appointments.
 * Used by both DashboardView and AppointmentManagementPage.
 * Implements DRY principle - single source of truth for appointment data types.
 */

import { AppointmentStatus } from '@/lib/api/appointments';

// Status badge colors - using FHIR status values directly
export const appointmentStatusStyles: Record<string, string> = {
  Fulfilled: 'text-green-600',
  Booked: 'text-blue-500',
  Pending: 'text-amber-500',
  Arrived: 'text-purple-500',
  Noshow: 'text-red-500',
  Cancelled: 'text-gray-500',
  'On Hold': 'text-orange-500',
  Waitlist: 'text-orange-500',
  'Entered-in-error': 'text-red-700',
  Proposed: 'text-cyan-500',
  // Lowercase versions for direct status matching
  fulfilled: 'text-green-600',
  booked: 'text-blue-500',
  pending: 'text-amber-500',
  arrived: 'text-purple-500',
  noshow: 'text-red-500',
  cancelled: 'text-gray-500',
};

// Shared appointment row data interface
export interface AppointmentRowData {
  id: string;
  patientName: string;
  patientId: string;
  /** Human-readable identifier (e.g. Book No.) from patient.identifier, used for display only */
  patientIdentifier?: string;
  age: number;
  gender: 'Male' | 'Female' | 'Other';
  contact: string;
  visitType: string;
  status: string;
  originalStatus: AppointmentStatus | string;
  appointmentStart?: string;
  appointmentEnd?: string;
  encounterId?: string;
  doctorName?: string;
  practitionerId?: string;
  imageUrl?: string;
  hasReport?: boolean;
  /** Whether this patient is new (first appointment ever) */
  isNewPatient?: boolean;
  /**
   * Quick-submit summary lifecycle, set only for encounters submitted via
   * Quick Submit that haven't been verified yet (appointment not `fulfilled`).
   * 'preparing' = submitted, agent hasn't written a DiagnosticReport yet.
   * 'pending_verification' = a DiagnosticReport now exists in Medplum, ready
   * for the doctor to review and save on the Case Sheet page.
   */
  summaryStatus?: 'preparing' | 'pending_verification';
}

// Format time for display (e.g., "09:30 AM")
export const formatAppointmentTime = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

// Format date for display in tables (e.g., "06 Feb")
export const formatAppointmentDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
  });
};

// Format date and time for display in tables (e.g., "06 Feb, 09:30 AM")
export const formatAppointmentDateTime = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = formatAppointmentDate(dateString);
  const time = formatAppointmentTime(dateString);
  return `${date}, ${time}`;
};

// Get status display text (capitalized)
export const getStatusDisplay = (status: string, originalStatus?: string): string => {
  const displayStatus = originalStatus || status;
  if (['on-hold', 'on hold'].includes(displayStatus.toLowerCase())) return 'On Hold';
  if (displayStatus.toLowerCase() === 'pending') return 'On Hold';
  return displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1).toLowerCase();
};

// Quick-submit summary status display labels (takes priority over the FHIR status pill)
export const summaryStatusLabels: Record<'preparing' | 'pending_verification', string> = {
  preparing: 'Preparing summary',
  pending_verification: 'Pending verification',
};

// Check if consultation is disabled for this appointment
export const isConsultationDisabled = (originalStatus: string | AppointmentStatus): boolean => {
  const status = originalStatus.toLowerCase();
  return ['fulfilled', 'cancelled', 'noshow'].includes(status);
};

// A started consultation that was put on hold: either still 'checked-in', or 'pending'
// ("On Hold") with an encounter. Deleting it would lose the in-progress patient record.
export const isDeleteLocked = (
  originalStatus: string | AppointmentStatus | undefined,
  encounterId?: string
): boolean => {
  const status = originalStatus?.toLowerCase();
  return status === 'checked-in' || (status === 'pending' && !!encounterId);
};

// Grid template for appointments table rows - using minmax for responsive spacing
export const APPOINTMENTS_TABLE_GRID =
  'grid-cols-[minmax(180px,2fr)_minmax(90px,1fr)_minmax(100px,1.2fr)_minmax(100px,1.2fr)_minmax(130px,1.5fr)_minmax(80px,1fr)_minmax(140px,1.2fr)]';

// Grid template for appointments table with doctor column (DMO view)
export const APPOINTMENTS_TABLE_GRID_WITH_DOCTOR =
  'grid-cols-[minmax(180px,2fr)_minmax(90px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(160px,2fr)]';

// Grid template for patient records table - using minmax for responsive spacing
export const PATIENTS_TABLE_GRID =
  'grid-cols-[minmax(180px,2fr)_minmax(100px,1fr)_minmax(100px,1.2fr)_minmax(100px,1.2fr)_minmax(200px,2fr)]';

// Grid template for practitioners table - 5 columns
export const PRACTITIONERS_TABLE_GRID =
  'grid-cols-[minmax(180px,2fr)_minmax(120px,1.5fr)_minmax(120px,1.5fr)_minmax(80px,1fr)_minmax(100px,1fr)]';

// Grid template for onboarding practitioners table - 6 columns (includes actions)
export const ONBOARDING_PRACTITIONERS_TABLE_GRID =
  'grid-cols-[minmax(180px,2fr)_minmax(140px,1.5fr)_minmax(120px,1.2fr)_minmax(120px,1.2fr)_minmax(90px,0.8fr)_minmax(120px,1fr)]';
